import React, { useEffect, useState } from 'react';
import moment from 'moment';
import DayNames from './components/DayNames';
import Week from './components/Week';
import Fa from '../Fa';

import './calendar.css';

export type RenderWeeksProps = {
  month: moment.Moment;
  handleSelect: (date: moment.Moment) => void;
} & Pick<CalendarComponentProps, 'clickable' | 'minDate' | 'selected'>;

function RenderWeeks({
  month,
  clickable,
  minDate,
  selected,
  handleSelect,
}: RenderWeeksProps): JSX.Element {
  const weeks = [];
  let done = false;
  const theDate = month.clone().startOf('month').startOf('isoWeek');
  let monthIndex = theDate.month();
  let count = 0;

  while (!done) {
    weeks.push(
      <Week
        bookings={[]}
        clickable={!!clickable}
        minDate={minDate}
        date={theDate.clone()}
        key={theDate.toString()}
        month={month}
        selected={selected}
        onDaySelect={(): void => handleSelect(theDate)}
      />,
    );
    theDate.add(1, 'w');
    done = (count += 1) > 2 && monthIndex !== theDate.month();
    monthIndex = theDate.month();
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{weeks}</>;
}

export interface CalendarComponentProps {
  minCurrentDay?: boolean;
  minDate?: moment.Moment;
  clickable?: boolean;
  selected: moment.Moment;
  onDateChange: (day: moment.Moment) => void;
  renderWeeks?: (props: RenderWeeksProps) => JSX.Element;
}

export default function CalendarComponent({
  minCurrentDay,
  minDate,
  clickable,
  selected,
  onDateChange,
  renderWeeks,
}: CalendarComponentProps): JSX.Element {
  function checkForPreviousOption(month: moment.Moment): boolean {
    if (minCurrentDay) {
      return month.isSame(moment(), 'month') && month.isSame(moment(), 'year');
    }
    return false;
  }

  function checkForNextOption(monthToCheck: moment.Moment): boolean {
    const endDate = moment('2026-01-09');
    return monthToCheck.isSame(endDate, 'month') && monthToCheck.isSame(endDate, 'year');
  }

  const date = selected || moment().startOf('day');
  const [month, setMonth] = useState(date.clone());
  const [previousDisabled, setPreviousDisabled] = useState(checkForPreviousOption(date));
  const [nextDisabled, setNextDisabled] = useState(checkForNextOption(date));

  function updatePreviousState(theMonth: moment.Moment): void {
    setPreviousDisabled(checkForPreviousOption(theMonth));
  }

  function updateNextState(theMonth: moment.Moment): void {
    setNextDisabled(checkForNextOption(theMonth));
  }

  function handlePrevious(): void {
    const theMonth = month.clone();
    theMonth.subtract(1, 'M');
    setMonth(theMonth);
    updatePreviousState(theMonth);
    updateNextState(theMonth);
  }

  function handleNext(): void {
    const theMonth = month.clone();
    theMonth.add(1, 'M');
    setMonth(theMonth);
    updatePreviousState(theMonth);
    updateNextState(theMonth);
  }

  function handleSelect(day: moment.Moment): void {
    if (clickable) {
      onDateChange(day);
    }
  }

  function renderMonthLabel(): JSX.Element {
    return (
      <span className="calendar-month-label">
        {month.locale(moment.locale()).format('MMMM YYYY')}
      </span>
    );
  }

  return (
    <div className="calendar">
      <div className="calendar-header">
        <div className="calendar-header-content">
          {renderMonthLabel()}
          <div className="calendar-icon-navigation">
            <button
              className="calendar-icon-prev"
              disabled={previousDisabled}
              onClick={handlePrevious}
              type="button"
            >
              <Fa icon="angle-left" />
            </button>
            <button
              className="calendar-icon-next"
              disabled={nextDisabled}
              onClick={handleNext}
              type="button"
            >
              <Fa icon="angle-right" />
            </button>
          </div>
        </div>
      </div>
      <DayNames />
      {renderWeeks ? (
        renderWeeks({
          month,
          minDate,
          selected,
          clickable,
          handleSelect: (theDate: moment.Moment): void => handleSelect(theDate),
        })
      ) : (
        <RenderWeeks
          month={month}
          minDate={minDate}
          selected={selected}
          clickable={clickable}
          handleSelect={(theDate: moment.Moment): void => handleSelect(theDate)}
        />
      )}
    </div>
  );
}

const defaultProps: Pick<CalendarComponentProps, 'minCurrentDay' | 'clickable' | 'renderWeeks'> = {
  minCurrentDay: false,
  clickable: true,
  renderWeeks: RenderWeeks,
};

CalendarComponent.defaultProps = defaultProps;
